import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, useFormContext } from 'react-hook-form'
import { CampaignSettings, CommunicationFrequency, PeriodType } from '@/api/dashboard'
import { ErrorMessage } from '@hookform/error-message'
import { FormErrorMessage, Input, Select, Toggle, formatHookFormErrors } from '@dashboard/ui'

interface FrequencyCardProps {
  label: ReactNode
  desc: ReactNode
  field: string
}

const defaultFrequencyValue: CommunicationFrequency = {
  period_type: PeriodType.Day,
  limit: 3,
}

export const FrequencyCard = ({ label, desc, field }: FrequencyCardProps) => {
  const { t } = useTranslation()

  const {
    register,
    formState: { errors },
    control,
    watch,
    setValue,
  } = useFormContext<CampaignSettings>()

  const typesOptions = Object.values(PeriodType).map(value => ({
    children: t(`intl.${value}`, { count: 1 }),
    value: value,
  }))

  const frequency = watch(field as 'email_frequency')

  const onClickToggle = (v: boolean) => {
    setValue(
      field as 'email_frequency',
      v
        ? {
            ...defaultFrequencyValue,
          }
        : (null as unknown as undefined),
      { shouldDirty: true },
    )
  }

  const limitField = `${field}.limit` as 'email_frequency.limit'
  const selected = !!frequency?.period_type
  return (
    <section className="rounded-xl bg-fg-primary p-6 text-text-primary transition-all duration-300 ease-in-out">
      <section className="mb-3 flex items-start justify-between border-b border-border-secondary pb-3">
        <p className="text-[18px] font-semibold">{label}</p>
        <Toggle checked={selected} size="sm" onChange={e => onClickToggle(e.target.checked)} />
      </section>
      <section className="flex items-start gap-10 overflow-hidden">
        <p className="max-w-[320px] text-text-secondary">{desc}</p>
        <div className="rounded-lg bg-fg-secondary p-6">
          <p className="mb-3 text-[16px] font-semibold">{t('campaign_settings.frequency.form.label')}</p>
          <div className="flex items-center gap-4">
            <Input
              disabled={!selected}
              {...register(limitField, {
                required: selected,
                min: { value: 1, message: t('campaign_settings.frequency.time-limit') },
                max: { value: 1000, message: t('campaign_settings.frequency.time-limit') },
              })}
              type="number"
              min={1}
              {...formatHookFormErrors(errors, limitField)}
            />
            <p className="text-[16px] font-semibold text-text-tertiary">
              {t('campaign_settings.frequency.form.perDivider')}
            </p>
            <Controller
              control={control}
              name={`${field}.period_type` as 'email_frequency.period_type'}
              rules={{ required: selected }}
              render={({ field }) => (
                <Select
                  datatestid="campaign-settings/email-frequency/periods"
                  disabled={!selected}
                  {...field}
                  items={typesOptions}
                />
              )}
            />
          </div>
          <ErrorMessage
            name={limitField}
            errors={errors}
            render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
          />
        </div>
      </section>
    </section>
  )
}
